import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TablePagination, TextField, useTheme, useMediaQuery, CardContent, Stack, InputAdornment, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress
} from '@mui/material';
import SearchableSelect from '../../components/SearchableSelect';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon, PlaylistAdd as PlaylistAddIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import sucVentaService from '../../services/sucVentaService';
import sucursalesVentaService, { SucursalVenta } from '../../services/sucursalesVentaService';

const SucVenta: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sucVentas, setSucVentas] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterNroSuc, setFilterNroSuc] = useState<number | null>(null);
  const [sucursales, setSucursales] = useState<SucursalVenta[]>([]);
  const [loadingSucursales, setLoadingSucursales] = useState(false);
  const [_loading, setLoading] = useState(false);
  const [aplicarDialogOpen, setAplicarDialogOpen] = useState(false);
  const [aplicarNroSuc, setAplicarNroSuc] = useState<number | null>(null);
  const [aplicandoTipos, setAplicandoTipos] = useState(false);

  const fetchSucVentas = useCallback(async () => {
    try {
      setLoading(true);
      const result = await sucVentaService.getSucVentas({
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        searchTerm: searchTerm,
        nroSuc: filterNroSuc ?? undefined,
      });
      setSucVentas(result.items || []);
      setTotalCount(result.totalCount || 0);
    } catch (error: any) {
      console.error('Error al cargar sucursales de venta:', error);
      const data = error?.response?.data;
      const msg = typeof data === 'string' ? data : data?.error ?? data?.message ?? data?.Message ?? 'Error al cargar sucursales de venta';
      enqueueSnackbar(msg, { variant: 'error' });
      setSucVentas([]);
      setTotalCount(0);
      if (msg?.includes('PageSize') && rowsPerPage > 20) setRowsPerPage(20);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, filterNroSuc, enqueueSnackbar]);

  useEffect(() => {
    const loadSucursales = async () => {
      try {
        setLoadingSucursales(true);
        const result = await sucursalesVentaService.getSucursalesVenta({ pageNumber: 1, pageSize: 100 });
        setSucursales(result.items || []);
      } catch (error) {
        console.error('Error al cargar sucursales:', error);
        enqueueSnackbar('No se pudieron cargar las sucursales', { variant: 'warning' });
      } finally {
        setLoadingSucursales(false);
      }
    };
    loadSucursales();
  }, [enqueueSnackbar]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchSucVentas();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm, filterNroSuc, fetchSucVentas]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = Math.min(20, Math.max(5, parseInt(event.target.value, 10) || 10));
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const sucursalFilterOptions = useMemo(
    () =>
      sucursales.map((sucursal) => ({
        value: sucursal.nroSuc,
        label: `${sucursal.nroSuc} - ${sucursal.nombreSucursal}`,
      })),
    [sucursales],
  );

  const handleDelete = async (nroSuc: number, nroTipoFormulario: string) => {
    if (window.confirm('¿Está seguro de eliminar esta asignación?')) {
      try {
        await sucVentaService.deleteSucVenta(nroSuc, nroTipoFormulario);
        enqueueSnackbar('Asignación eliminada exitosamente', { variant: 'success' });
        fetchSucVentas();
      } catch (error: any) {
        console.error('Error al eliminar asignación:', error);
        enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar asignación', { variant: 'error' });
      }
    }
  };

  const handleOpenAplicarDialog = () => {
    setAplicarNroSuc(filterNroSuc);
    setAplicarDialogOpen(true);
  };

  const handleCloseAplicarDialog = () => {
    if (aplicandoTipos) return;
    setAplicarDialogOpen(false);
    setAplicarNroSuc(null);
  };

  const handleAplicarTodosTipos = async () => {
    if (!aplicarNroSuc) {
      enqueueSnackbar('Seleccione una sucursal', { variant: 'warning' });
      return;
    }

    try {
      setAplicandoTipos(true);
      const result = await sucVentaService.aplicarTodosTiposFormulario(aplicarNroSuc);

      if (result.creados > 0) {
        enqueueSnackbar(
          `Se crearon ${result.creados} asignación${result.creados === 1 ? '' : 'es'}. ${result.omitidos > 0 ? `${result.omitidos} ya existían.` : ''}`.trim(),
          { variant: 'success' },
        );
        setFilterNroSuc(aplicarNroSuc);
        setPage(0);
        const refreshed = await sucVentaService.getSucVentas({
          pageNumber: 1,
          pageSize: rowsPerPage,
          searchTerm,
          nroSuc: aplicarNroSuc,
        });
        setSucVentas(refreshed.items || []);
        setTotalCount(refreshed.totalCount || 0);
      } else if (result.omitidos > 0 && result.errores.length === 0) {
        enqueueSnackbar('La sucursal ya tiene asignados todos los tipos de formulario', { variant: 'info' });
      }

      if (result.errores.length > 0) {
        const detalle = result.errores
          .slice(0, 3)
          .map((e) => `${e.nroTipoFormulario}: ${e.message}`)
          .join(' · ');
        enqueueSnackbar(
          `No se pudieron crear ${result.errores.length} asignación${result.errores.length === 1 ? '' : 'es'}. ${detalle}`,
          { variant: 'error' },
        );
      }

      if (result.creados > 0 || (result.omitidos > 0 && result.errores.length === 0)) {
        setAplicarDialogOpen(false);
        setAplicarNroSuc(null);
      }
    } catch (error: any) {
      console.error('Error al aplicar tipos de formulario:', error);
      const data = error?.response?.data;
      const msg =
        typeof data === 'string'
          ? data
          : data?.error ?? data?.message ?? data?.Message ?? 'Error al aplicar tipos de formulario';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setAplicandoTipos(false);
    }
  };

  const sucursalAplicarLabel = useMemo(() => {
    if (!aplicarNroSuc) return '';
    const sucursal = sucursales.find((s) => s.nroSuc === aplicarNroSuc);
    return sucursal ? `${sucursal.nroSuc} - ${sucursal.nombreSucursal}` : String(aplicarNroSuc);
  }, [aplicarNroSuc, sucursales]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Tipos de formulario por Sucursal
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<PlaylistAddIcon />}
                onClick={handleOpenAplicarDialog}
              >
                Aplicar todos los tipos de formulario
              </Button>
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/configuraciones/sucventa/nuevo')}>
                Nueva Asignación
              </Button>
            </Stack>
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <SearchableSelect<number>
              label="Sucursal"
              value={filterNroSuc}
              onChange={(val) => {
                setFilterNroSuc(val);
                setPage(0);
              }}
              options={sucursalFilterOptions}
              allowEmpty
              emptyOptionLabel="Todas"
              loading={loadingSucursales}
              size="small"
              sx={{
                minWidth: { xs: '100%', sm: 280 },
                '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' },
              }}
            />
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Stack>

          {isMobile ? (
            <Stack spacing={2}>
              {sucVentas.map((sv) => (
                <Card key={`${sv.nroSuc}-${sv.nroTipoFormulario}`} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Suc: {sv.nroSuc} - {sv.nombreSucursal || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Tipo: {sv.nroTipoFormulario} - {sv.tipoFormulario?.descripcion || 'N/A'}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => navigate(`/configuraciones/sucventa/${sv.nroSuc}/${sv.nroTipoFormulario}`)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(sv.nroSuc, sv.nroTipoFormulario)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>N° Sucursal</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Sucursal</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>N° Tipo Formulario</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Tipo Formulario</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sucVentas.map((sv) => (
                    <TableRow key={`${sv.nroSuc}-${sv.nroTipoFormulario}`} hover>
                      <TableCell>{sv.nroSuc}</TableCell>
                      <TableCell>{sv.nombreSucursal || 'N/A'}</TableCell>
                      <TableCell>{sv.nroTipoFormulario}</TableCell>
                      <TableCell>{sv.tipoFormulario?.descripcion || 'N/A'}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => navigate(`/configuraciones/sucventa/${sv.nroSuc}/${sv.nroTipoFormulario}`)} color="primary">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => handleDelete(sv.nroSuc, sv.nroTipoFormulario)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 20]}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={aplicarDialogOpen} onClose={handleCloseAplicarDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Aplicar todos los tipos de formulario</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Seleccione una sucursal para crear automáticamente una asignación con cada tipo de formulario
              existente. Los tipos ya asignados a esa sucursal se omitirán.
            </Typography>
            <SearchableSelect<number>
              label="Sucursal"
              value={aplicarNroSuc}
              onChange={setAplicarNroSuc}
              options={sucursalFilterOptions}
              required
              allowEmpty
              emptyOptionLabel="Seleccionar sucursal"
              loading={loadingSucursales}
              disabled={aplicandoTipos}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            {aplicarNroSuc ? (
              <Typography variant="body2" color="text.secondary">
                Sucursal seleccionada: <strong>{sucursalAplicarLabel}</strong>
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseAplicarDialog} disabled={aplicandoTipos}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleAplicarTodosTipos}
            disabled={!aplicarNroSuc || aplicandoTipos}
            startIcon={aplicandoTipos ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {aplicandoTipos ? 'Aplicando...' : 'Aplicar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SucVenta;
